// Âm thanh của thông báo donate theo kiểu: kêu to hơn mức streamer đặt và nhạc mở màn dựng sẵn.
//
// HTMLMediaElement.volume tối đa là 1, nên "to hơn 10/20 điểm" khi streamer đã để 100% phải đi qua Web Audio:
// MediaElementSource → GainNode (có thể > 1) → DynamicsCompressor chỉnh như bộ giới hạn → loa.
// Web Audio chỉ đọc được âm thanh cùng nguồn (cùng tên miền, data:, blob:) hoặc máy chủ có CORS; nguồn khác sẽ ra im lặng.
// Vì vậy: ≤ 100% thì chỉ đặt .volume như trước; > 100% mới dùng Web Audio, file Dropbox được tải qua
// dl.dropboxusercontent.com (máy chủ này trả Access-Control-Allow-Origin: *) rồi phát bằng blob cùng nguồn.
// Bước nào hỏng cũng quay về .volume = min(1, …): thông báo không bao giờ bị mất tiếng vì phần này.

import { isAllowedAssetUrl } from './donate-alert';
import { INTRO_MS, IntroStyle, kindGain } from './alert-kind';

export type VolumeRoute = { mode: 'element'; volume: number } | { mode: 'webaudio'; gain: number };

/** Chọn cách đặt âm lượng: ≤ 1 hoặc không dùng được Web Audio thì đặt .volume (tối đa 1). */
export const planVolumeRoute = (gain: number, canWebAudio: boolean): VolumeRoute => {
  const g = Number.isFinite(gain) ? Math.max(0, gain) : 1;
  if (g <= 1 || !canWebAudio) return { mode: 'element', volume: Math.min(1, g) };
  return { mode: 'webaudio', gain: g };
};

/** Nguồn đọc được bằng Web Audio mà không cần CORS: cùng tên miền với trang, data: hoặc blob:. */
export const isSameOriginSrc = (src: string | null | undefined, origin: string): boolean => {
  if (!src) return false;
  try {
    const u = new URL(src, origin);
    if (u.protocol === 'data:' || u.protocol === 'blob:') return true;
    return u.origin === origin;
  } catch {
    return false;
  }
};

/**
 * Link file Dropbox của Cab đổi sang dl.dropboxusercontent.com để tải có CORS (www.dropbox.com không trả
 * Access-Control-Allow-Origin). Không phải Dropbox của Cab thì null (không tải, quay về .volume).
 */
export const corsFetchUrl = (src: string | null | undefined): string | null => {
  if (!src || !isAllowedAssetUrl(src)) return null;
  const u = new URL(src);
  if (u.hostname === 'www.dropbox.com') u.hostname = 'dl.dropboxusercontent.com';
  return u.toString();
};

// Bộ giới hạn: ngưỡng -3 dB, đầu gối 0, tỉ lệ 20, bắt 3 ms, nhả 100 ms.
export const LIMITER = { threshold: -3, knee: 0, ratio: 20, attack: 0.003, release: 0.1 };

type Ctor = typeof AudioContext;
let shared: AudioContext | null = null;

/** Một AudioContext dùng chung cho cả trang; trình duyệt không có Web Audio thì null. */
export const getAudioContext = (): AudioContext | null => {
  if (shared) return shared;
  try {
    const w = window as unknown as { AudioContext?: Ctor; webkitAudioContext?: Ctor };
    const C = w.AudioContext || w.webkitAudioContext;
    if (!C) return null;
    shared = new C();
    return shared;
  } catch {
    return null;
  }
};

/** Chỉ cho test. */
export const resetAudioContextForTest = (): void => {
  shared = null;
};

const sleep = (ms: number) =>
  new Promise<void>((r) => {
    setTimeout(r, ms);
  });

/** AudioContext đang chạy (hoặc chạy được trong thời gian ngắn). Bị trình duyệt giữ lại thì false để quay về .volume. */
export const ensureRunning = async (ctx: AudioContext, timeoutMs = 400): Promise<boolean> => {
  if (ctx.state === 'running') return true;
  try {
    await Promise.race([ctx.resume(), sleep(timeoutMs)]);
  } catch {
    // bỏ qua
  }
  // Đọc lại sau await: TypeScript vẫn giữ kiểu thu hẹp từ lần kiểm tra trước nên phải ép kiểu.
  return (ctx.state as AudioContextState) === 'running';
};

interface Chain {
  input: GainNode;
  dispose: () => void;
}

const buildLimiter = (ctx: AudioContext, gain: number): Chain => {
  const g = ctx.createGain();
  g.gain.value = gain;
  const c = ctx.createDynamicsCompressor();
  c.threshold.value = LIMITER.threshold;
  c.knee.value = LIMITER.knee;
  c.ratio.value = LIMITER.ratio;
  c.attack.value = LIMITER.attack;
  c.release.value = LIMITER.release;
  g.connect(c);
  c.connect(ctx.destination);
  return {
    input: g,
    dispose: () => {
      try {
        g.disconnect();
        c.disconnect();
      } catch {
        // bỏ qua
      }
    },
  };
};

// File tối đa ở media-service: mp3 3MB, ghi âm 15 giây; trần rộng hơn để không chặn nhầm.
export const MAX_BLOB_BYTES = 15 * 1024 * 1024;

/**
 * Chỉ thay nguồn bằng blob khi nội dung đúng là âm thanh: kiểu audio/*, video/webm (ghi âm), hoặc
 * application/octet-stream, và cỡ hợp lý. Khác thì giữ link gốc (phát 100% thay vì có thể hỏng tiếng).
 */
export const isPlayableBlob = (contentType: string, size: number): boolean => {
  const t = (contentType || '').split(';')[0].trim().toLowerCase();
  const typeOk = t.startsWith('audio/') || t === 'video/webm' || t === 'application/octet-stream';
  return typeOk && size > 0 && size <= MAX_BLOB_BYTES;
};

const fetchBlobUrl = async (url: string, signal?: AbortSignal, timeoutMs = 2500): Promise<string | null> => {
  if (typeof fetch !== 'function' || typeof URL.createObjectURL !== 'function') return null;
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), timeoutMs);
  const onAbort = () => ac.abort();
  signal?.addEventListener('abort', onAbort);
  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit', signal: ac.signal });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!isPlayableBlob(blob.type || res.headers?.get?.('content-type') || '', blob.size)) return null;
    return URL.createObjectURL(blob);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onAbort);
  }
};

const NOOP = () => undefined;

const setElementVolume = (el: HTMLMediaElement, v: number) => {
  const media = el; // đổi thuộc tính của thẻ được truyền vào là đúng ý đồ của hàm
  try {
    media.volume = Math.min(1, Math.max(0, v));
  } catch {
    // bỏ qua
  }
};

/**
 * Đặt âm lượng cho một thẻ audio theo mức streamer đặt cộng mức to hơn của kiểu, gọi TRƯỚC play().
 * Trả về hàm dọn dẹp (ngắt nút Web Audio, thu hồi blob) để gọi khi thông báo kết thúc.
 */
export const routeAlertVolume = async (
  el: HTMLMediaElement,
  volume: unknown,
  boost: number,
  signal?: AbortSignal
): Promise<() => void> => {
  const media = el;
  const gain = kindGain(volume, boost);
  if (planVolumeRoute(gain, true).mode === 'element') {
    setElementVolume(media, gain);
    return NOOP;
  }
  // Mặc định an toàn trước: to nhất mà .volume cho phép. Các bước dưới hỏng thì giữ nguyên mức này.
  setElementVolume(media, 1);
  const ctx = getAudioContext();
  if (!ctx || !(await ensureRunning(ctx)) || signal?.aborted) return NOOP;

  let blobUrl: string | null = null;
  if (!isSameOriginSrc(media.src, window.location.origin)) {
    const corsUrl = corsFetchUrl(media.src);
    if (!corsUrl) return NOOP;
    blobUrl = await fetchBlobUrl(corsUrl, signal);
    if (!blobUrl) return NOOP;
    if (signal?.aborted) {
      URL.revokeObjectURL(blobUrl);
      return NOOP;
    }
  }
  const revoke = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  };

  let chain: Chain | null = null;
  let source: MediaElementAudioSourceNode | null = null;
  try {
    chain = buildLimiter(ctx, gain);
    if (blobUrl) media.src = blobUrl;
    source = ctx.createMediaElementSource(media);
    source.connect(chain.input);
  } catch {
    // Đã tạo nguồn mà nối không được thì nối thẳng ra loa, không để thẻ audio câm.
    if (source) {
      try {
        source.connect(ctx.destination);
      } catch {
        // bỏ qua
      }
    }
    chain?.dispose();
    return revoke;
  }
  const s = source;
  const c = chain;
  return () => {
    try {
      s.disconnect();
    } catch {
      // bỏ qua
    }
    c.dispose();
    revoke();
  };
};

// ── Nhạc mở màn dựng sẵn (chờ file của designer) ──

export interface IntroNote {
  f: number;
  at: number;
  dur: number;
  type: OscillatorType;
  level: number;
}

const C3 = 130.81;
const C4 = 261.63;
const G4 = 392.0;
const C5 = 523.25;
const E5 = 659.25;
const G5 = 783.99;
const C6 = 1046.5;
const C7 = 2093.0;

const chord = (fs: number[], at: number, dur: number, type: OscillatorType, level: number): IntroNote[] =>
  fs.map((f) => ({ f, at, dur, type, level }));

/** Nốt của hai đoạn mở màn: 2 giây đi lên cho Nổi bật, 4 giây kèn lệnh cho Đặc biệt. */
export const INTRO_NOTES: Record<IntroStyle, IntroNote[]> = {
  short: [
    { f: C5, at: 0, dur: 0.35, type: 'triangle', level: 0.22 },
    { f: E5, at: 0.15, dur: 0.35, type: 'triangle', level: 0.22 },
    { f: G5, at: 0.3, dur: 0.35, type: 'triangle', level: 0.22 },
    { f: C6, at: 0.45, dur: 0.5, type: 'triangle', level: 0.22 },
    ...chord([C5, E5, G5, C6], 0.8, 1.15, 'triangle', 0.14),
    { f: C7, at: 0.8, dur: 0.6, type: 'sine', level: 0.05 },
  ],
  long: [
    { f: G4, at: 0, dur: 0.13, type: 'sawtooth', level: 0.16 },
    { f: G4, at: 0.16, dur: 0.13, type: 'sawtooth', level: 0.16 },
    { f: G4, at: 0.32, dur: 0.13, type: 'sawtooth', level: 0.16 },
    { f: C5, at: 0.48, dur: 0.55, type: 'sawtooth', level: 0.18 },
    { f: E5, at: 1.05, dur: 0.25, type: 'sawtooth', level: 0.16 },
    { f: G5, at: 1.32, dur: 0.25, type: 'sawtooth', level: 0.16 },
    { f: C6, at: 1.6, dur: 0.9, type: 'sawtooth', level: 0.16 },
    ...chord([C4, G4, C5, E5, G5, C6], 2.55, 1.4, 'sawtooth', 0.09),
    { f: C3, at: 2.55, dur: 1.4, type: 'sine', level: 0.25 },
    { f: C7, at: 2.6, dur: 1.2, type: 'sine', level: 0.04 },
  ],
};

export interface IntroHandle {
  done: Promise<void>;
  stop: () => void;
}

const DONE: IntroHandle = { done: Promise.resolve(), stop: NOOP };

// Mức chung của nhạc mở màn so với tiếng thông báo (các file âm thanh có sẵn vốn đã to).
const INTRO_LEVEL = 0.8;

const playSynthIntro = (style: IntroStyle, gain: number): IntroHandle => {
  const ctx = getAudioContext();
  if (!ctx) return DONE;
  let stopped = false;
  let chain: Chain | null = null;
  const oscs: OscillatorNode[] = [];
  const stop = () => {
    stopped = true;
    oscs.forEach((o) => {
      try {
        o.stop();
      } catch {
        // bỏ qua
      }
    });
    chain?.dispose();
    chain = null;
  };
  const done = (async () => {
    if (!(await ensureRunning(ctx)) || stopped) return;
    try {
      chain = buildLimiter(ctx, gain * INTRO_LEVEL);
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 2600;
      filter.connect(chain.input);
      const t0 = ctx.currentTime + 0.05;
      INTRO_NOTES[style].forEach((n) => {
        const o = ctx.createOscillator();
        const env = ctx.createGain();
        o.type = n.type;
        o.frequency.value = n.f;
        const start = t0 + n.at;
        const end = start + n.dur;
        env.gain.setValueAtTime(0.0001, start);
        env.gain.exponentialRampToValueAtTime(n.level, start + 0.015);
        env.gain.setValueAtTime(n.level, Math.max(start + 0.02, end - n.dur * 0.6));
        env.gain.exponentialRampToValueAtTime(0.0001, end);
        o.connect(env);
        env.connect(filter);
        o.start(start);
        o.stop(end + 0.02);
        oscs.push(o);
      });
    } catch {
      stop();
      return;
    }
    await sleep(INTRO_MS[style] + 50);
    if (!stopped) chain?.dispose();
  })();
  return { done, stop };
};

const playFileIntro = (url: string, volume: unknown, boost: number, style: IntroStyle): IntroHandle => {
  let audio: HTMLAudioElement | null = null;
  let cleanup: () => void = NOOP;
  const ac = new AbortController();
  const stop = () => {
    ac.abort();
    if (audio) audio.pause();
    cleanup();
  };
  const done = new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    // File designer dài bao nhiêu cũng không giữ thông báo quá lâu.
    setTimeout(finish, INTRO_MS[style] + 4000);
    try {
      audio = new Audio(url);
      const a = audio;
      a.onended = finish;
      a.onerror = finish;
      routeAlertVolume(a, volume, boost, ac.signal)
        .then((c) => {
          cleanup = c;
          if (ac.signal.aborted) return finish();
          return a.play().catch(finish);
        })
        .catch(finish);
    } catch {
      finish();
    }
  });
  return { done, stop };
};

/**
 * Phát nhạc mở màn ở mức đã cộng của kiểu, xong mới phát tiếng thông báo. Có file designer (Dropbox của Cab) thì phát file,
 * không thì phát đoạn dựng sẵn bằng Web Audio. Không có Web Audio hoặc bị trình duyệt chặn thì bỏ qua (done xong ngay).
 */
export const playIntro = (
  intro: { style: IntroStyle; url: string | null } | null,
  volume: unknown,
  boost: number
): IntroHandle => {
  if (!intro) return DONE;
  // Tiếng thông báo đã tắt hẳn thì không phát nhạc mở màn.
  if (kindGain(volume, boost) <= 0) return DONE;
  try {
    if (intro.url && isAllowedAssetUrl(intro.url)) return playFileIntro(intro.url, volume, boost, intro.style);
    return playSynthIntro(intro.style, kindGain(volume, boost));
  } catch {
    return DONE;
  }
};
